<template>
    <div v-if="show && loaded" class="nevs-popup-container">
        <div class="nevs-popup assign-things-popup">
            <div class="assign-things-content">
                <h2 class="assign-things-title">{{ $LANG.Get('labels.assignThingsTitle') }}</h2>
                <div class="assign-things-disclaimer">
                    <i class="fa-solid fa-triangle-exclamation"></i>
                    <span>{{ $LANG.Get('texts.assignThingsDisclaimer') }}</span>
                </div>
                <div class="assign-things-body">
                    <table class="assign-things-table">
                        <thead>
                        <tr>
                            <th>{{ $LANG.Get('fields.item') }}</th>
                            <th>{{ $LANG.Get('labels.person') }}</th>
                            <th>{{ $LANG.Get('fields.startDate') }}</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(entry, key) in localEntries" :key="key">
                            <td class="assign-things-name">{{ entry.name }}</td>
                            <td>
                                <NevsSelect v-model="entry.person_id" :ajax="'select/persons'" :nullable="true"></NevsSelect>
                            </td>
                            <td>
                                <NevsDateField v-if="entry.type === 'membership'" v-model="entry.start_date"></NevsDateField>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div class="nevs-popup-buttons">
                <NevsButton class="error" @click="skipClick()">
                    <i class="fa-solid fa-forward"></i>
                    {{ $LANG.Get('buttons.skip') }}
                </NevsButton>
                <NevsButton class="success" @click="okClick()">
                    <i class="fa-solid fa-floppy-disk"></i>
                    {{ $LANG.Get('buttons.save') }}
                </NevsButton>
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import NevsButton from "@/components/nevs/NevsButton.vue";
import NevsDateField from "@/components/nevs/NevsDateField.vue";
import NevsSelect from "@/components/nevs/NevsSelect.vue";

export default {
    name: "ModalAssignInvoiceThings",
    components: {NevsSelect, NevsDateField, NevsButton},
    props: {
        show: {
            type: Boolean,
            default: false
        },
        invoiceId: {
            type: [Number, String],
            default: null
        },
        entries: {
            type: Array,
            default: () => []
        },
        defaultPersonId: {
            type: [Number, String],
            default: null
        }
    },
    emits: ['done'],
    data() {
        return {
            loaded: false,
            localEntries: [],
            // Each person's memberships as loaded for this popup, keyed by person id, so picking the same person
            // on several rows asks the server once.
            personMemberships: {}
        }
    },
    watch: {
        show() {
            this.loaded = false;
            if (this.show) {
                let today = moment().format('YYYY-MM-DD');
                this.personMemberships = {};
                this.localEntries = this.entries.map((entry) => {
                    return {
                        type: entry.type,
                        thing_id: entry.thing_id,
                        name: entry.name,
                        person_id: (this.defaultPersonId !== null && this.defaultPersonId !== undefined) ? this.defaultPersonId : null,
                        start_date: entry.type === 'membership' ? today : null,
                        // The person the start date was last worked out for; see localEntries below.
                        start_date_person_id: null
                    };
                });
                this.$nextTick(() => {
                    this.loaded = true;
                });
            }
        },
        // Whenever a membership row gets a (different) person, its start date moves to the first day that person
        // has no membership running. Staff can still change it afterwards; it is only reset when the person is.
        localEntries: {
            deep: true,
            handler() {
                for (let entry of this.localEntries) {
                    if (entry.type !== 'membership' || entry.person_id === entry.start_date_person_id) continue;
                    entry.start_date_person_id = entry.person_id;
                    this.resolveStartDate(entry);
                }
            }
        }
    },
    methods: {
        resolveStartDate(entry) {
            let personId = entry.person_id;
            if (personId === null || personId === '') {
                entry.start_date = moment().format('YYYY-MM-DD');
                return;
            }
            let apply = (records) => {
                // The person may have been changed again while the memberships were loading.
                if (entry.person_id === personId) entry.start_date = this.$HELPERS.FirstFreeMembershipDate(records);
            };
            if (this.personMemberships[personId] !== undefined) {
                apply(this.personMemberships[personId]);
                return;
            }
            let vm = this;
            this.$API.APICall('get', 'person-memberships/by-person/' + personId, {}, (data, success) => {
                // A failed lookup leaves today, as before.
                let records = success && Array.isArray(data.records) ? data.records : [];
                vm.personMemberships[personId] = records;
                apply(records);
            }, false);
        },
        skipClick() {
            this.$emit('done');
        },
        okClick() {
            let assignments = [];
            for (let entry of this.localEntries) {
                if (entry.person_id === null || entry.person_id === '') {
                    continue;
                }
                let assignment = {
                    type: entry.type,
                    person_id: entry.person_id
                };
                if (entry.type === 'membership') {
                    assignment.membership_id = entry.thing_id;
                    assignment.start_date = entry.start_date;
                } else {
                    assignment.package_id = entry.thing_id;
                }
                assignments.push(assignment);
            }

            if (assignments.length === 0) {
                this.$emit('done');
                return;
            }

            let vm = this;
            this.$API.APICall('post', 'invoices/assign-things', {
                invoice_id: this.invoiceId,
                assignments: assignments
            }, (data, success) => {
                if (success) {
                    vm.$emit('done');
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        }
    }
}
</script>

<style scoped lang="scss">
@use '../../scss/vars' as *;

.assign-things-popup {
    width: 60%;
    max-width: 800px;
}

.assign-things-content {
    padding: 25px 25px 5px;
}

.assign-things-title {
    margin: 0 0 15px;
    font-size: 1.4em;
}

.assign-things-disclaimer {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background-color: $warning-surface;
    border: 1px solid $warning-border;
    color: $warning-ink;
    border-radius: 5px;
    padding: 12px 14px;
    margin-bottom: 20px;
    font-size: 0.9em;
    line-height: 1.45;
}

.assign-things-disclaimer i {
    margin-top: 2px;
    flex-shrink: 0;
}

.assign-things-body {
    max-height: 50vh;
    overflow-y: auto;
}

.assign-things-table {
    width: 100%;
    border-collapse: collapse;
}

.assign-things-table th {
    text-align: left;
    padding: 0 15px 10px 0;
    border-bottom: 2px solid $sand-400;
    font-size: 0.8em;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.assign-things-table td {
    text-align: left;
    padding: 10px 15px 10px 0;
    border-bottom: 1px solid $sand-300;
    vertical-align: middle;
}

.assign-things-table th:last-child, .assign-things-table td:last-child {
    padding-right: 0;
}

.assign-things-name {
    font-weight: 600;
}

@media only screen and (max-width: 850px) {
    .assign-things-popup {
        width: 90%;
    }
}
</style>
